import { z } from 'zod';

// Motif accepté pour les valeurs numériques optionnelles exposées en string (chaîne vide tolérée).
const optionalNumericString = (message: string) =>
  z
    .string()
    .refine(value => value === '' || /^\d+$/.test(value), message)
    .optional();

// Les appels peuvent utiliser une URL absolue ou un chemin relatif lorsque le proxy local/nginx
// prend en charge le routage, par exemple `/api`.
const absoluteOrRelativeUrl = (message: string) =>
  z.string().refine(value => {
    if (/^\/(?!\/)\S*$/.test(value)) {
      return true;
    }

    return z.url().safeParse(value).success;
  }, message);

// Schéma de la configuration d'environnement runtime (ENV). Les URLs backend et l'identité
// CloudID sont indispensables au fonctionnement de l'application ; le reste est optionnel
// (télémétrie). Voir la décision "avertissement, pas de blocage" dans le playbook zod-validation.
export const envSchema = z.object({
  APP_ENV: z.string().min(1, 'VITE_APP_ENV est obligatoire'),
  DEBUG_MODE: z.boolean(),
  LAYOUT_ERROR: z.boolean(),
  DSN_MULTI_SORT_ENABLED: z.boolean(),
  BACK_EDI_URL: absoluteOrRelativeUrl(
    'VITE_BACK_EDI_URL doit être une URL valide ou un chemin relatif',
  ),
  CLOUDID_DOMAIN: z.string().min(1, 'VITE_CLOUDID_DOMAIN est obligatoire'),
  CLOUDID_ACCOUNT_URL: z.url('VITE_CLOUDID_ACCOUNT_URL doit être une URL valide'),
  CLOUDID_CLIENT_ID: z.string().min(1, 'VITE_CLOUDID_CLIENT_ID est obligatoire'),
  CLOUDID_AUDIENCE: z.string().min(1, 'VITE_CLOUDID_AUDIENCE est obligatoire'),
  LOGGER_INSIGHT_CONNECTION_STRING: z.string().optional(),
  NEWRELIC_LICENSE_KEY: z.string().optional(),
  NEWRELIC_ACCOUNT_ID: z.string().optional(),
  NEWRELIC_TRUST_KEY: z.string().optional(),
  NEWRELIC_APP_ID: z.string().optional(),
  KEY_TOKEN_EXPIRATION: optionalNumericString('VITE_KEY_TOKEN_EXPIRATION doit être numérique'),
  DATE_RANGE_DEFAULT_LENGTH: optionalNumericString(
    'VITE_DATE_RANGE_DEFAULT_LENGTH doit être numérique',
  ),
  DATE_RANGE_MAX_LENGTH: optionalNumericString('VITE_DATE_RANGE_MAX_LENGTH doit être numérique'),
});

export type EnvConfig = z.infer<typeof envSchema>;

// Valide la config et retourne uniquement des messages (chemin de clé + raison), jamais les valeurs.
export function collectEnvValidationErrors(env: unknown): string[] {
  const result = envSchema.safeParse(env);
  if (result.success) {
    return [];
  }
  return result.error.issues.map(issue => `${issue.path.join('.')}: ${issue.message}`);
}
